import clsx from 'clsx';
import React, { memo, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { useEventTracking } from '@hooks';
import { Article } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { ImpressionElement, impression } from '@tracking';
import {
  MINIMUM_RECOMMENDED_ARTICLES,
  getBlueconicRecommendations,
} from '@utils/blueconic/recommendations';

import StoryItemMidArticle from './StoryItem';

interface RelatedStoriesMidArticleProps {
  articles: Article[];
  className?: string;
  headline?: string;
}

export const articleContinuesBelowClasses =
  'before:absolute before:-top-28 before:w-full before:text-center before:font-suisse-intl-mono before:text-overline-sm before:text-grayscale-gray before:content-["Article_continues_below"] sm:before:hidden';

const RelatedStoriesMidArticle = ({
  articles,
  className,
  headline = 'Recommended',
}: RelatedStoriesMidArticleProps): JSX.Element => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.RelatedStoriesMidArticle,
  });
  const [articleRecommendation, setArticleRecommendation] = useState<Article[]>([]);

  useEffect(() => {
    const fetchBlueconicRecommendations = async () => {
      try {
        const blueconicRecommendations: Article[] = await getBlueconicRecommendations();

        if (blueconicRecommendations.length >= MINIMUM_RECOMMENDED_ARTICLES) {
          setArticleRecommendation(blueconicRecommendations);
        } else {
          setArticleRecommendation(articles);
        }
      } catch (error) {
        console.error('Error fetching mid-article recommendations from BlueConic:', error);
      }
    };

    fetchBlueconicRecommendations();
  }, [articles]);

  return articles.length ? (
    <Track>
      <aside
        className={clsx(
          'relative mx-0 mb-32 mt-[60px] w-full sm:float-right sm:mb-[46px] sm:ml-36 sm:mt-24 sm:max-w-[445px] md:-mr-[267px] md:ml-48 md:max-w-[462px] lg:-mr-[302px] lg:max-w-[550px] xl:-mr-[342px] print:hidden',
          articleContinuesBelowClasses,
          className
        )}
        data-testid="related-stories-mid-article"
      >
        <InView
          className="min-h-[336px] border-0 border-b border-t border-solid border-black pb-10 pt-12 sm:min-h-[760px] sm:py-28 md:min-h-[620px]"
          threshold={0.5}
          triggerOnce={true}
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: ImpressionElement.RelatedStories }));
          }}
        >
          <header>
            <h2 className="m-0 font-suisse-intl-mono text-feed-headline-sm sm:text-feed-headline-lg">
              {headline}
            </h2>
          </header>
          <div className="mt-10 flex w-full gap-[45px] overflow-x-auto sm:mt-24 sm:flex-col sm:gap-48 md:flex-row md:flex-wrap md:gap-x-36">
            {articleRecommendation.length > 0 &&
              articleRecommendation.map((article, index) => (
                <StoryItemMidArticle
                  key={`mid-article-recirc-${article.id}`}
                  {...article}
                  index={index}
                />
              ))}
          </div>
        </InView>
      </aside>
    </Track>
  ) : null;
};

export default memo(RelatedStoriesMidArticle);
